import { useContext, useEffect, useRef, useState } from "react";

import { CounterData } from "components/TableDetails/CompanyDetails/types/interfaces";
import ParamsContext from "context/params/paramsContext";
import { COUNTER_INITIAL_STATE } from "shared/constants/resources";
import { API_IS_BEING_CANCELLED } from "shared/constants/strings";
import { FILTER_NAMES, LINK_IDS } from "shared/types/enums";
import { FilterId } from "shared/types/types";

import { useApi } from "../useApi";
import { useIsMounted } from "../useIsMounted";
import { getStatisticsEndpoint } from "./functions";

export const useCounterData = (
    id: FilterId[] | number | undefined,
    doesHaveSameRootId?: boolean,
    doesIncludeHierarchy?: boolean
) => {
    const { queryParams } = useContext(ParamsContext);

    const { cancelSource, isCanceled, getData, handleResponse } = useApi();
    const isMounted = useIsMounted();
    const isInitialLoad = useRef(true);

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [counter, setCounter] = useState<CounterData>(COUNTER_INITIAL_STATE);

    const { Users, Groups, Files, Devices, Tasks, ChildCompanies } = LINK_IDS;

    const rootCompanyId = queryParams[FILTER_NAMES.RootCompany];

    const statisticsEndpoint = getStatisticsEndpoint(
        rootCompanyId,
        doesHaveSameRootId,
        id,
        doesIncludeHierarchy
    );

    useEffect(() => {
        const getCounterData = async () => {
            try {
                setIsLoading(true);

                const {
                    data: {
                        user_count,
                        group_count,
                        file_count,
                        device_count,
                        task_count,
                        company_count
                    }
                } = await getData(statisticsEndpoint);

                setCounter({
                    [Users]: user_count,
                    [Groups]: group_count,
                    [Files]: file_count,
                    [Devices]: device_count,
                    [Tasks]: task_count,
                    [ChildCompanies]: company_count
                });

                setIsLoading(false);
            } catch (error) {
                if (isMounted && !isCanceled(error)) {
                    handleResponse(error);
                    setCounter(COUNTER_INITIAL_STATE);
                    setIsLoading(false);
                }
            }

            if (isInitialLoad.current) {
                isInitialLoad.current = false;
            }
        };

        if (isMounted && id !== undefined) {
            getCounterData();
        }

        return () => {
            cancelSource.cancel(API_IS_BEING_CANCELLED);
        };

        // eslint-disable-next-line
    }, [isMounted, statisticsEndpoint]);

    return { isInitialLoad: isInitialLoad.current, isLoading, counter };
};
